import {
  AppstoreAddOutlined,
  EditOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Form } from 'antd';
import { useContext, useState } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { RedirectIcon } from 'app/components/pages/Hotels/RedirectIcon';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import type {
  PackResponse,
  PackRoomResponse,
} from 'app/typings/core/admin/packs/pack';
import {
  formatDayRoomAccessDuration,
  formatDayRoomAccessWindow,
  getHotelDayRoomAccessPath,
} from 'app/utils/dayRoomAccess';

import '../ExperiencesAndRooms.scss';

import { AddRoomsModal } from './AddRoomsModal/AddRoomsModal';
import { SortableRoomsList } from './SortableRoomsList';

export const PackRooms = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      packRooms: PackRoomResponse[];
      isDaytime: boolean;
    }
  >();
  const formValues = Form.useWatch([], form);
  const isDaytime = formValues?.isDaytime ?? false;
  const hotelId = formValues?.hotelId;

  const { data: hotel } = useGetHotel(hotelId, {
    refetchOnWindowFocus: isDaytime ? 'always' : false,
  });
  const dayRoomAccess = isDaytime ? hotel?.dayRoomAccess : undefined;
  const isDayRoomAccessMissing = isDaytime && !dayRoomAccess;
  const dayRoomAccessPath =
    hotelId !== undefined ? getHotelDayRoomAccessPath(hotelId) : undefined;
  const addRoomsModalTitle = dayRoomAccess
    ? `Add Rooms (day access • ${formatDayRoomAccessDuration(
        dayRoomAccess.durationMinutes
      )})`
    : undefined;

  const [isModalVisible, setIsModalVisible] = useState(false);

  const openModal = () => setIsModalVisible(true);
  const closeModal = () => setIsModalVisible(false);

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const current: PackRoomResponse[] = form.getFieldValue('packRooms') || [];

    const reordered = arrayMove(current, oldIndex, newIndex).map((e, idx) => ({
      ...e,
      priority: idx,
    }));

    form.setFieldsValue({ packRooms: reordered });
    form.validateFields();
  };

  return (
    <div className="er-card er-card-rooms">
      <div className="er-header-row">
        {isDaytime ? (
          <div className="er-header-title-group">
            <div className="er-header-title">Rooms • day access</div>
            {dayRoomAccess ? (
              <a
                className="er-header-subtitle er-header-subtitle-link"
                href={dayRoomAccessPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatDayRoomAccessWindow(dayRoomAccess)}
                <EditOutlined />
              </a>
            ) : (
              <div className="er-header-subtitle">Setup required</div>
            )}
          </div>
        ) : (
          <div className="er-header-title">Rooms</div>
        )}
        <Button
          disabled={!isEditMode || isDayRoomAccessMissing}
          icon={<PlusOutlined />}
          onClick={openModal}
        >
          Add room
        </Button>
      </div>

      <FormLineLayout>
        <Form.Item name="packRooms" noStyle />
      </FormLineLayout>

      {formValues?.packRooms.length > 0 ? (
        <SortableRoomsList
          rooms={[...formValues?.packRooms].sort(
            (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
          )}
          isEditMode={isEditMode}
          onSortEnd={onSortEnd}
        />
      ) : isDayRoomAccessMissing ? (
        <EmptyState
          title="Setup required to add rooms"
          description="Set the duration and time window before adding day rooms to this package"
          icon={<SettingOutlined />}
          action={
            <Button
              className="er-setup-room-access"
              href={dayRoomAccessPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              Set up room access
              <RedirectIcon />
            </Button>
          }
        />
      ) : isDaytime ? (
        <EmptyState
          title="No room access"
          description="No rooms have been added to this package for day access."
          icon={<AppstoreAddOutlined />}
        />
      ) : (
        <EmptyState
          title="No rooms"
          description="Associate a room to this package"
          icon={<AppstoreAddOutlined />}
        />
      )}

      <AddRoomsModal
        visible={isModalVisible}
        onClose={closeModal}
        title={addRoomsModalTitle}
      />
    </div>
  );
};
